import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { LoadingPhasesBox } from "./ResponseWaiting";
import { RenderResponseString } from "./utils";
import { getSegmentsCached } from "./messageSegmentsParser";

const tableHeaderTransition = {
  duration: 0.24,
  ease: [0.22, 1, 0.36, 1],
};

const getTableRowTransition = (idx) => ({
  duration: 0.26,
  delay: Math.min(idx, 7) * 0.035,
  ease: [0.22, 1, 0.36, 1],
});

const getTableRenderKey = (spec, segmentIndex, variantIndex = 0) => {
  const title = String(spec?.title || "").trim();
  return `tbl-${segmentIndex}-${variantIndex}-${title}`;
};

const clipboardCellText = (value) => {
  if (value == null) return "";

  const text =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  return text.replace(/[\t\r\n]+/g, " ").trim();
};

const tableClipboardText = (spec) => {
  const cols = Array.isArray(spec?.columns) ? spec.columns : [];
  const rows = Array.isArray(spec?.rows) ? spec.rows : [];
  if (cols.length === 0) return "";

  return [
    cols.map((col) => clipboardCellText(col?.label ?? col?.key)).join("\t"),
    ...rows.map((row) =>
      cols.map((col) => clipboardCellText(row?.[col?.key])).join("\t"),
    ),
  ].join("\n");
};

function CodeBlockRenderer({ code, lang, isDark }) {
  const [copied, setCopied] = useState(false);

  const colors = isDark
    ? {
        border: "#2b2f36",
        headFrom: "rgba(255,255,255,0.08)",
        headTo: "rgba(255,255,255,0.04)",
        bg: "rgba(255,255,255,0.03)",
        codeBg: "rgba(0,0,0,0.35)",
        text: "#e8eaed",
        subtext: "#b3b7bd",
        shadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
        ln: "rgba(255,255,255,0.35)",
      }
    : {
        border: "#e5e7eb",
        headFrom: "#DAE3F7",
        headTo: "#DAE3F7",
        bg: "#ffffff",
        codeBg: "rgba(0,0,0,0.03)",
        text: "#111827",
        subtext: "#6b7280",
        shadow: "0 1px 1px rgba(0,0,0,0.03)",
        ln: "rgba(0,0,0,0.45)",
      };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  };

  const lines = String(code ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n");

  return (
    <Box
      sx={{
        mt: 1.25,
        mb: 1.75,
        border: `1px solid ${colors.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        bgcolor: colors.bg,
        boxShadow: colors.shadow,
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: `linear-gradient(180deg, ${colors.headFrom}, ${colors.headTo})`,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box
          sx={{
            fontSize: "0.78rem",
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: colors.subtext,
            textTransform: "uppercase",
          }}
        >
          {lang || "code"}
        </Box>
        <Box
          component="button"
          onClick={handleCopy}
          aria-label="Copy code"
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: colors.text,
            background: "transparent",
            border: `1px solid ${colors.border}`,
            borderRadius: "8px",
            px: 1,
            py: 0.4,
            cursor: "pointer",
            transition: "all 120ms ease",
            "&:hover": { backgroundColor: colors.codeBg },
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 1,
          maxHeight: "520px",
          overflow: "auto",
          backgroundColor: colors.bg,
        }}
      >
        <Box
          sx={{
            backgroundColor: isDark
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.015)",
            borderRight: `1px solid ${colors.border}`,
            px: 1,
            py: 1,
            userSelect: "none",
            textAlign: "right",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: "0.82rem",
            lineHeight: 1.6,
            color: colors.ln,
          }}
        >
          {lines.map((_, i) => (
            <Box key={i}>{i + 1}</Box>
          ))}
        </Box>

        <Box
          component="pre"
          sx={{
            m: 0,
            px: 1.25,
            py: 1,
            overflow: "auto",
            whiteSpace: "pre",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: "0.82rem",
            lineHeight: 1.6,
            color: colors.text,
            backgroundColor: "transparent",
            fontVariantLigatures: "none",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <code>{code}</code>
        </Box>
      </Box>
    </Box>
  );
}

const TableRenderer = React.memo(
  function TableRenderer({ spec, isDark, pageSize = 8 }) {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [copyHovered, setCopyHovered] = useState(false);
    const copyTimeoutRef = useRef(null);

    const cols = Array.isArray(spec?.columns) ? spec.columns : [];
    const rows = Array.isArray(spec?.rows) ? spec.rows : [];

    const showToggle = rows.length > pageSize;
    const visibleRows = expanded ? rows : rows.slice(0, pageSize);
    const remaining = Math.max(0, rows.length - pageSize);

    useEffect(
      () => () => {
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      },
      [],
    );

    const handleCopy = async () => {
      const clipboardText = tableClipboardText(spec);
      if (!clipboardText) return;

      try {
        await navigator.clipboard.writeText(clipboardText);
        setCopied(true);
        if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = setTimeout(() => {
          setCopied(false);
          copyTimeoutRef.current = null;
        }, 1200);
      } catch (_) {
        setCopied(false);
      }
    };

    const colors = isDark
      ? {
          border: "#2b2f36",
          headFrom: "rgba(255,255,255,0.10)",
          headTo: "rgba(255,255,255,0.06)",
          rowOdd: "rgba(255,255,255,0.03)",
          rowHover: "rgba(255,255,255,0.06)",
          bg: "rgba(255,255,255,0.02)",
          text: "#e8eaed",
          subtext: "#b3b7bd",
          colDivider: "rgba(255,255,255,0.06)",
          btnBg: "rgba(255,255,255,0.03)",
          btnText: "#f5f5f5",
        }
      : {
          border: "#e5e7eb",
          headBg: "#D9E2F8",
          headFrom: "#D9E2F8",
          headTo: "#D9E2F8",
          rowOdd: "rgba(0,0,0,0.015)",
          rowHover: "rgb(217, 226, 248, 0.7)",
          bg: "#ffffff",
          text: "#111827",
          subtext: "#4b5563",
          colDivider: "rgba(0,0,0,0.06)",
          btnBg: "#fff",
          btnText: "#111",
        };

    const cellBaseSx = {
      px: 1.25,
      py: 1,
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text,
      fontSize: "0.875rem",
      lineHeight: 1.6,
      fontVariantNumeric: "tabular-nums",
      fontFeatureSettings: '"tnum" 1',
      whiteSpace: "nowrap",
    };

    return (
      <Box
        data-chat-table-container
        onMouseLeave={() => setCopyHovered(false)}
        sx={{
          mt: 1,
          mb: 1.5,
          "&:focus-within [data-chat-table-copy]": {
            opacity: 1,
            pointerEvents: "auto",
            transform: "translateY(0)",
          },
        }}
      >
        <Box
          data-chat-table-toolbar
          sx={{
            mb: 0.75,
            minHeight: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {spec?.title ? (
            <Box
              data-chat-table-title
              sx={{
                minWidth: 0,
                fontWeight: 600,
                fontSize: "0.95rem",
                color: colors.text,
              }}
            >
              {spec.title}
            </Box>
          ) : (
            <Box aria-hidden="true" />
          )}
          {cols.length > 0 && (
            <Box
              component="button"
              type="button"
              aria-label={`Copy ${spec?.title ? `${spec.title} ` : ""}table`}
              data-chat-table-copy
              onClick={handleCopy}
              sx={{
                flex: "0 0 auto",
                height: "24px",
                px: 0.75,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.45,
                border: `1px solid ${colors.border}`,
                borderRadius: "7px",
                backgroundColor: colors.btnBg,
                color: colors.subtext,
                fontSize: "0.6875rem",
                fontWeight: 600,
                lineHeight: 1,
                cursor: "pointer",
                opacity: copyHovered ? 1 : 0,
                pointerEvents: copyHovered ? "auto" : "none",
                transform: copyHovered ? "translateY(0)" : "translateY(-2px)",
                transition:
                  "opacity 120ms ease, transform 120ms ease, background-color 120ms ease, color 120ms ease",
                "&:hover": {
                  backgroundColor: colors.rowHover,
                  color: colors.text,
                },
                "&:focus-visible": {
                  outline: `2px solid ${isDark ? "#93c5fd" : "#2563eb"}`,
                  outlineOffset: "2px",
                },
                "@media (hover: none)": {
                  opacity: 1,
                  pointerEvents: "auto",
                  transform: "translateY(0)",
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "background-color 120ms ease, color 120ms ease",
                  transform: "none",
                },
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 12 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 12 }} />
              )}
              <Box component="span">{copied ? "Copied" : "Copy table"}</Box>
            </Box>
          )}
        </Box>

        <TableContainer
          data-chat-table-surface
          onMouseEnter={() => setCopyHovered(true)}
          sx={{
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            overflowX: "auto",
            bgcolor: colors.bg,
            boxShadow: isDark
              ? "inset 0 1px 0 rgba(255,255,255,0.04)"
              : "0 1px 1px rgba(0,0,0,0.02)",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                component={motion.tr}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={tableHeaderTransition}
                sx={{
                  "& th": {
                    ...cellBaseSx,
                    fontWeight: 700,
                    color: colors.text,
                    background:
                      colors.headBg ||
                      `linear-gradient(180deg, ${colors.headFrom}, ${colors.headTo})`,
                    backgroundColor: colors.headBg || colors.headFrom,
                    backdropFilter: "blur(4px)",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  },
                  "& th.MuiTableCell-stickyHeader": {
                    background:
                      colors.headBg ||
                      `linear-gradient(180deg, ${colors.headFrom}, ${colors.headTo})`,
                    backgroundColor: colors.headBg || colors.headFrom,
                  },
                  "& th:first-of-type": { borderTopLeftRadius: "12px" },
                  "& th:last-of-type": { borderTopRightRadius: "12px" },
                }}
              >
                {cols.map((c, idx) => (
                  <TableCell
                    key={c.key ?? idx}
                    align={c.align || "left"}
                    sx={{
                      ...cellBaseSx,
                      fontWeight: 700,
                      background:
                        colors.headBg ||
                        `linear-gradient(180deg, ${colors.headFrom}, ${colors.headTo})`,
                      backgroundColor: colors.headBg || colors.headFrom,
                      ...(idx > 0
                        ? { borderLeft: `1px solid ${colors.colDivider}` }
                        : null),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        color: colors.text,
                        letterSpacing: "0.01em",
                      }}
                    >
                      {c.label ?? c.key}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.map((r, i) => (
                <TableRow
                  key={i}
                  component={motion.tr}
                  hover
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={getTableRowTransition(i)}
                  sx={{
                    "& td": { ...cellBaseSx },
                    "&:nth-of-type(odd) td": { backgroundColor: colors.rowOdd },
                    "&:hover td": { backgroundColor: colors.rowHover },
                    transition: "background-color 120ms ease",
                  }}
                >
                  {cols.map((c, idx) => {
                    const align = c.align || "left";
                    return (
                      <TableCell
                        key={c.key ?? idx}
                        align={align}
                        sx={{
                          ...(idx > 0
                            ? { borderLeft: `1px solid ${colors.colDivider}` }
                            : null),
                          ...(align === "right" ? { pr: 1.25, pl: 1 } : null),
                          ...(c.muted ? { color: colors.subtext } : null),
                        }}
                      >
                        {r?.[c.key] ?? ""}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {showToggle && (
          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                padding: "6px 10px",
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                background: colors.btnBg,
                color: colors.btnText,
                cursor: "pointer",
              }}
            >
              {expanded
                ? "Show less"
                : `See more${remaining ? ` (${remaining})` : ""}`}
            </button>
          </Box>
        )}
      </Box>
    );
  },
  (prev, next) =>
    prev.isDark === next.isDark &&
    prev.pageSize === next.pageSize &&
    prev.spec === next.spec,
);

function StreamingTextFade({ text, compactSpecialSpacing }) {
  return RenderResponseString({
    messageThread: { message: text },
    compactSpecialSpacing,
  });
}

const MessageSegments = React.memo(
  function MessageSegments({
    msgId,
    raw,
    isDark,
    isStreaming,
    compactSpecialSpacing = false,
  }) {
    const segments = useMemo(() => getSegmentsCached(msgId, raw), [msgId, raw]);

    return (
      <>
        {segments.map((seg, i) => {
          if (seg.type === "text") {
            return (
              <StreamingTextFade
                key={`txt-${i}`}
                text={seg.text}
                compactSpecialSpacing={compactSpecialSpacing}
              />
            );
          }

          if (seg.type === "table") {
            const isLastSegment = i === segments.length - 1;
            if (isStreaming && isLastSegment) {
              return (
                <LoadingPhasesBox
                  key={`tbl-loading-${i}`}
                  text={"Generating table"}
                  sx={{ m: 0 }}
                  hideDotDotDot={false}
                />
              );
            }
            return (
              <TableRenderer
                key={getTableRenderKey(seg.spec, i)}
                spec={seg.spec}
                isDark={isDark}
              />
            );
          }

          if (seg.type === "code") {
            return (
              <CodeBlockRenderer
                key={`code-${i}`}
                code={seg.code}
                lang={seg.lang}
                isDark={isDark}
              />
            );
          }

          if (seg.type === "pending-table") {
            if (isStreaming) {
              return (
                <LoadingPhasesBox
                  key={`ptbl-loading-${i}`}
                  text={"Generating table"}
                  sx={{ m: 0 }}
                  hideDotDotDot={false}
                />
              );
            }

            if (Array.isArray(seg.specs) && seg.specs.length) {
              return (
                <React.Fragment key={`ptbl-${i}`}>
                  {seg.specs.map((spec, idx) => (
                    <TableRenderer
                      key={getTableRenderKey(spec, i, idx)}
                      spec={spec}
                      isDark={isDark}
                    />
                  ))}
                </React.Fragment>
              );
            }

            return (
              <CodeBlockRenderer
                key={`ptblraw-${i}`}
                code={seg.body}
                lang="json"
                isDark={isDark}
              />
            );
          }

          if (seg.type === "pending-code") {
            if (isStreaming) {
              return (
                <LoadingPhasesBox
                  key={`pcode-loading-${i}`}
                  text={"Generating code"}
                  sx={{ m: 0 }}
                  hideDotDotDot={false}
                />
              );
            }

            return (
              <CodeBlockRenderer
                key={`pcoderaw-${i}`}
                code={seg.body}
                lang={seg.lang || "text"}
                isDark={isDark}
              />
            );
          }

          return null;
        })}
      </>
    );
  },
  (prev, next) =>
    prev.msgId === next.msgId &&
    prev.raw === next.raw &&
    prev.isDark === next.isDark &&
    prev.isStreaming === next.isStreaming &&
    prev.compactSpecialSpacing === next.compactSpecialSpacing,
);

export default MessageSegments;
export { tableClipboardText };
